import { Plus, RotateCw, Search } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button, Checkbox, Input } from "~/components/ui";
import type { RelationTypeRow } from "~/features/admin/relationTypes/types";
import { RelationTypeFormDialog } from "./RelationTypeFormDialog";
import { RelationTypePeek } from "./RelationTypePeek";
import { RelationTypesTable } from "./RelationTypesTable";

interface RelationTypesPresenterProps {
  rows: RelationTypeRow[];
  search: string;
  onSearchChange: (next: string) => void;
  excludeDeactivated: boolean;
  onExcludeDeactivatedChange: (next: boolean) => void;
  pageIndex: number;
  onPageIndexChange: (next: number) => void;
  pageSize: number;
  onPageSizeChange: (next: number) => void;
  onRefresh: () => void;
  selectedRelationType: string | null;
  onSelect: (id: string) => void;
  onCloseSelected: () => void;
}

export function RelationTypesPresenter({
  rows,
  search,
  onSearchChange,
  excludeDeactivated,
  onExcludeDeactivatedChange,
  pageIndex,
  onPageIndexChange,
  pageSize,
  onPageSizeChange,
  onRefresh,
  selectedRelationType,
  onSelect,
  onCloseSelected,
}: RelationTypesPresenterProps) {
  const { t } = useTranslation();
  const [createOpen, setCreateOpen] = useState(false);

  const filteredRows = useMemo(
    () => (excludeDeactivated ? rows.filter((row) => !row.isDeactivated) : rows),
    [rows, excludeDeactivated],
  );

  return (
    <div className="mx-auto max-w-[var(--content-max)] space-y-5 p-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <h1 className="text-dewey-title capitalize">{t("model.relationType.modelNames")}</h1>
        <div className="flex flex-wrap items-center gap-2.5">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-fg-3" />
            <Input
              value={search}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder={t("ui.search")}
              className="h-9 w-[240px] pl-8"
              aria-label={t("ui.search")}
            />
          </div>
          <Button type="button" variant="outline" size="icon" onClick={onRefresh} aria-label={t("ui.reload")}>
            <RotateCw />
          </Button>
          <label
            htmlFor="relation-types-exclude-deactivated"
            className="inline-flex items-center gap-2 text-14-regular text-fg-2"
          >
            <Checkbox
              id="relation-types-exclude-deactivated"
              checked={excludeDeactivated}
              onCheckedChange={(v) => onExcludeDeactivatedChange(v === true)}
            />
            <span>{t("ui.excludeDeactivated")}</span>
          </label>
          <Button type="button" size="sm" onClick={() => setCreateOpen(true)}>
            <Plus />
            <span>{t("ui.add")}</span>
          </Button>
        </div>
      </div>

      <RelationTypeFormDialog open={createOpen} onOpenChange={setCreateOpen} />

      <RelationTypesTable
        rows={filteredRows}
        onSelect={onSelect}
        emptyMessage={t("ui.notFound")}
        globalFilter={search}
        onGlobalFilterChange={onSearchChange}
        pageIndex={pageIndex}
        onPageIndexChange={onPageIndexChange}
        pageSize={pageSize}
        onPageSizeChange={onPageSizeChange}
      />

      <RelationTypePeek id={selectedRelationType} onClose={onCloseSelected} />
    </div>
  );
}
